import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStats } from "@/lib/api";
import { ScanLine, ClipboardList, Activity, TrendingUp, ChevronRight, FileCheck2, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const Stat = ({ label, value, sub, icon: Icon, tone = "indigo", testId }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 hover:border-zinc-800 transition-colors"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
        {label}
      </span>
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center ${
          tone === "rose"
            ? "bg-rose-500/10 text-rose-400"
            : tone === "emerald"
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-indigo-500/10 text-indigo-400"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
    <div className="font-display text-4xl font-semibold tracking-tight text-zinc-50">
      {value}
    </div>
    {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
            <span className="live-dot" />
            <span className="font-mono tracking-tight">SYSTEM ONLINE · RF-DETR NAS BASE</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-50 leading-[1.05]">
            Second Opinion AI
            <span className="block text-zinc-500 font-normal text-2xl sm:text-3xl mt-2">
              untuk deteksi abnormalitas X-ray thoraks MCU
            </span>
          </h1>
        </div>
        <Link
          to="/analyze"
          data-testid="dashboard-cta-analyze"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-3 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-all self-start lg:self-auto"
        >
          <ScanLine className="w-4 h-4" strokeWidth={2} />
          Analisa X-ray Baru
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 bg-zinc-900" />
          ))
        ) : (
          <>
            <Stat
              label="Total Pemeriksaan"
              value={stats?.total_exams ?? 0}
              sub="Sejak sistem aktif"
              icon={ClipboardList}
              tone="indigo"
              testId="stat-total"
            />
            <Stat
              label="Temuan Abnormal"
              value={stats?.with_findings ?? 0}
              sub="Perlu review radiolog"
              icon={AlertTriangle}
              tone="rose"
              testId="stat-findings"
            />
            <Stat
              label="Class Teratas"
              value={stats?.top_findings?.[0]?.class ?? "—"}
              sub={
                stats?.top_findings?.[0]
                  ? `${stats.top_findings[0].count} deteksi`
                  : "Belum ada data"
              }
              icon={TrendingUp}
              tone="indigo"
              testId="stat-top-class"
            />
          </>
        )}
      </div>

      {/* Two-col: recent + findings */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Recent exams */}
        <div className="xl:col-span-2 rounded-xl border border-zinc-900 bg-zinc-900/40">
          <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                Recent
              </div>
              <div className="font-display text-lg font-medium text-zinc-100 mt-0.5">
                Pemeriksaan Terbaru
              </div>
            </div>
            <Link
              to="/history"
              data-testid="link-view-all-history"
              className="text-xs text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1"
            >
              Lihat semua <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-900">
            {(stats?.recent ?? []).length === 0 && !loading && (
              <div className="p-8 text-center text-sm text-zinc-500">
                Belum ada pemeriksaan tersimpan. Klik &quot;Analisa X-ray Baru&quot; untuk memulai.
              </div>
            )}
            {(stats?.recent ?? []).map((exam) => (
              <Link
                key={exam.id}
                to={`/exam/${exam.id}`}
                data-testid={`recent-exam-${exam.id}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-zinc-900/60 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {exam.patient?.name || "Unnamed Patient"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    {exam.patient?.patient_id || "no-id"} ·{" "}
                    {new Date(exam.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {(() => {
                    const abnormal = (exam.predictions || []).filter((p) => p.class !== "normal").length;
                    return (
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                          abnormal > 0
                            ? "bg-rose-500/10 text-rose-400"
                            : "bg-emerald-500/10 text-emerald-400"
                        }`}
                      >
                        {abnormal > 0
                          ? `${abnormal} finding${abnormal > 1 ? "s" : ""}`
                          : "Normal"}
                      </span>
                    );
                  })()}
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Top findings */}
        <div className="rounded-xl border border-zinc-900 bg-zinc-900/40">
          <div className="px-5 py-4 border-b border-zinc-900">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Distribution
            </div>
            <div className="font-display text-lg font-medium text-zinc-100 mt-0.5">
              Temuan Terbanyak
            </div>
          </div>
          <div className="p-5 space-y-3">
            {(stats?.top_findings ?? []).length === 0 && (
              <div className="text-sm text-zinc-500 text-center py-6">
                Belum cukup data.
              </div>
            )}
            {(stats?.top_findings ?? []).map((f, i) => {
              const total = stats?.top_findings?.[0]?.count || 1;
              const pct = Math.round((f.count / total) * 100);
              return (
                <div key={f.class + i} data-testid={`finding-bar-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-zinc-200 tracking-tight">{f.class}</span>
                    <span className="text-xs font-mono text-zinc-500">{f.count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-10 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
        <Activity className="w-4 h-4 text-amber-400 mt-0.5" />
        <div className="text-xs text-amber-200/80 leading-relaxed">
          <strong className="text-amber-300">Disclaimer:</strong> Hasil AI adalah{" "}
          <em>second opinion</em> untuk membantu screening cepat MCU. Diagnosis
          dan laporan final tetap merupakan tanggung jawab Radiolog atau Dokter
          yang berwenang.
        </div>
      </div>
    </div>
  );
}
